import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, Star } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
  RowActions,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Account = {
  id: number
  name: string
  website: string
  industry: string
  size: '' | 'SOLO' | 'SMALL' | 'MEDIUM' | 'LARGE' | 'ENTERPRISE'
  annual_revenue: string | null
  phone: string
  email: string
  address_line1: string
  city: string
  postal_code: string
  country_code: string
  lifecycle_stage: 'PROSPECT' | 'CUSTOMER' | 'PARTNER' | 'LOST'
  partner: number | null
  partner_name: string | null
  owner: number | null
  owner_username: string | null
  notes: string
  contact_count: number

  // Relationship sentiment
  sentiment: '' | 'LOVES_US' | 'SUPPORTIVE' | 'NEUTRAL' | 'AVOID'
  sentiment_notes: string
  sentiment_updated_at: string | null
  sentiment_updated_by: number | null
  sentiment_updated_by_username: string | null
  sentiment_is_stale: boolean

  // Compliance / Do Not Contact (warning only, not enforced)
  do_not_contact: boolean
  do_not_contact_reason: string
}

type Contact = {
  id: number
  first_name: string
  last_name: string
  full_name: string
  email: string
  phone: string
  mobile: string
  job_title: string
  account: number | null
  account_name: string | null
  is_primary: boolean
  owner: number | null
  owner_username: string | null
  notes: string
  is_active: boolean
}

type Opportunity = {
  id: number
  name: string
  account: number
  account_name: string
  amount: string
  currency_code: string
  pipeline_name: string
  stage_name: string
  stage_color: string
  probability: string
  weighted_amount: string
  expected_close_date: string | null
  owner: number | null
  owner_username: string | null
  status: 'OPEN' | 'WON' | 'LOST'
}

// ─────────────────── Constants ───────────────────
const STAGE_COLOR: Record<Account['lifecycle_stage'], string> = {
  PROSPECT: 'bg-blue-100 text-blue-800',
  CUSTOMER: 'bg-green-100 text-green-800',
  PARTNER: 'bg-purple-100 text-purple-800',
  LOST: 'bg-red-100 text-red-700',
}

const SENTIMENT_COLOR: Record<Exclude<Account['sentiment'], ''>, string> = {
  LOVES_US:   'bg-emerald-100 text-emerald-800',
  SUPPORTIVE: 'bg-blue-100 text-blue-800',
  NEUTRAL:    'bg-gray-100 text-gray-700',
  AVOID:      'bg-orange-100 text-orange-800',
}

const SENTIMENT_OPTIONS = [
  { value: 'LOVES_US',   label: 'Loves us' },
  { value: 'SUPPORTIVE', label: 'Supportive' },
  { value: 'NEUTRAL',    label: 'Neutral' },
  { value: 'AVOID',      label: 'Avoid' },
] as const

const SIZE_LABEL: Record<string, string> = {
  SOLO: '1 employee',
  SMALL: '2-50',
  MEDIUM: '51-250',
  LARGE: '251-1,000',
  ENTERPRISE: '1,000+',
}

const OPP_STATUS_COLOR: Record<Opportunity['status'], string> = {
  OPEN: 'bg-blue-100 text-blue-800',
  WON: 'bg-green-100 text-green-800',
  LOST: 'bg-red-100 text-red-700',
}

type TabKey = 'details' | 'contacts' | 'opportunities' | 'files'

// ─────────────────── Page ───────────────────
export default function CRMAccountDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const accountId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const accountQ = useQuery({
    queryKey: ['crm', 'account', accountId],
    queryFn: async () => (await api.get<Account>(`/crm/accounts/${accountId}/`)).data,
    enabled: !!accountId,
  })

  const opportunitiesQ = useQuery({
    queryKey: ['crm', 'opportunities', { account: accountId }],
    queryFn: async () => (await api.get<Opportunity[]>(`/crm/opportunities/?account=${accountId}`)).data,
    enabled: !!accountId,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Account>) =>
      (await api.patch(`/crm/accounts/${accountId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'account', accountId] })
      qc.invalidateQueries({ queryKey: ['crm', 'accounts'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/crm/accounts/${accountId}/`),
    onSuccess: () => {
      toast.success('Account deleted')
      qc.invalidateQueries({ queryKey: ['crm', 'accounts'] })
      navigate('/admin/crm/accounts')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (accountQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (accountQ.isError || !accountQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Account not found.</p>
          <Link to="/admin/crm/accounts" className="text-blue-600 hover:underline">← Back to accounts</Link>
        </div>
      </AdminLayout>
    )
  }

  const account = accountQ.data
  const opportunities = opportunitiesQ.data || []

  const save = (payload: Partial<Account>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/crm/accounts" className="text-muted-foreground hover:text-foreground hover:underline">← Accounts</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-foreground">
              <InlineText value={account.name} onSave={(v) => save({ name: v })} />
            </h2>
            <div className="text-sm text-muted-foreground">
              {account.industry || <span className="italic">No industry</span>}
              {account.size && <span> · {SIZE_LABEL[account.size]}</span>}
              {account.country_code && <span> · <span className="font-mono">{account.country_code}</span></span>}
              {account.website && (
                <>
                  {' · '}
                  <a href={account.website} target="_blank" rel="noopener noreferrer"
                    className="text-blue-600 hover:underline">{account.website}</a>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={account.lifecycle_stage}
              colorMap={STAGE_COLOR}
              options={[
                { value: 'PROSPECT', label: 'Prospect' },
                { value: 'CUSTOMER', label: 'Customer' },
                { value: 'PARTNER', label: 'Partner' },
                { value: 'LOST', label: 'Lost' },
              ]}
              onSave={(v) => save({ lifecycle_stage: v })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm(
                    `account ${account.name}`,
                    () => deleteMut.mutate(),
                  )}
                  className="text-destructive"
                >
                  Delete account
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Do Not Contact warning — visible, not enforced on outreach code paths */}
      {account.do_not_contact && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white text-xs font-bold">!</span>
          <div className="flex-1 text-sm">
            <p className="font-semibold text-red-800">Do Not Contact</p>
            <p className="text-red-700">
              This account is flagged. Do not send marketing or automated outreach.
              {account.do_not_contact_reason && (
                <> Reason: <span className="italic">{account.do_not_contact_reason}</span></>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'contacts', 'opportunities', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'contacts' && `Contacts (${account.contact_count})`}
              {t === 'opportunities' && `Opportunities (${opportunities.length})`}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && <DetailsTab account={account} onSave={save} />}
      {tab === 'contacts' && <ContactsTab accountId={account.id} />}
      {tab === 'opportunities' && <OpportunitiesTab opportunities={opportunities} loading={opportunitiesQ.isLoading} />}
      {tab === 'files' && <FilesTab parent="crm.account" parentId={accountId} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  account, onSave,
}: {
  account: Account
  onSave: (payload: Partial<Account>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">
      <Section title="Identity">
        <Row label="Account name">
          <InlineText value={account.name} onSave={(v) => onSave({ name: v })} />
        </Row>
        <Row label="Website">
          <InlineText value={account.website} onSave={(v) => onSave({ website: v })} placeholder="https://…" />
        </Row>
        <Row label="Industry">
          <InlineText value={account.industry} onSave={(v) => onSave({ industry: v })} />
        </Row>
        <Row label="Company size">
          <InlineSelect
            value={account.size}
            displayLabel={account.size ? SIZE_LABEL[account.size] : <Dash />}
            allowClear
            options={[
              { value: 'SOLO', label: 'SOLO — 1 employee' },
              { value: 'SMALL', label: 'SMALL — 2-50' },
              { value: 'MEDIUM', label: 'MEDIUM — 51-250' },
              { value: 'LARGE', label: 'LARGE — 251-1,000' },
              { value: 'ENTERPRISE', label: 'ENTERPRISE — 1,000+' },
            ]}
            onSave={(v) => onSave({ size: v as Account['size'] })}
          />
        </Row>
        <Row label="Annual revenue">
          <InlineNumber
            value={account.annual_revenue || ''}
            onSave={(v) => onSave({ annual_revenue: v === '' ? null : v } as Partial<Account>)}
            displayFormat={(v) => v ? <span className="font-mono">{Number(v).toFixed(2)}</span> : <Dash />}
          />
        </Row>
        <Row label="Lifecycle stage">
          <InlineStatus
            value={account.lifecycle_stage}
            colorMap={STAGE_COLOR}
            options={[
              { value: 'PROSPECT', label: 'Prospect' },
              { value: 'CUSTOMER', label: 'Customer' },
              { value: 'PARTNER', label: 'Partner' },
              { value: 'LOST', label: 'Lost' },
            ]}
            onSave={(v) => onSave({ lifecycle_stage: v })}
          />
        </Row>
      </Section>

      <Section title="Relationship">
        <Row label="Sentiment">
          <div className="flex items-center gap-2">
            {account.sentiment ? (
              <InlineStatus
                value={account.sentiment}
                colorMap={SENTIMENT_COLOR}
                options={SENTIMENT_OPTIONS as any}
                onSave={(v) => onSave({ sentiment: v as Account['sentiment'] })}
              />
            ) : (
              <InlineSelect
                value=""
                allowClear
                options={SENTIMENT_OPTIONS as any}
                onSave={(v) => onSave({ sentiment: (v || '') as Account['sentiment'] })}
                placeholder="Set sentiment"
              />
            )}
            {account.sentiment_is_stale && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">
                Stale — review
              </span>
            )}
          </div>
        </Row>
        <Row label="Sentiment notes">
          <InlineText
            value={account.sentiment_notes}
            multiline
            onSave={(v) => onSave({ sentiment_notes: v })}
            placeholder="Why this sentiment; recent context"
          />
        </Row>
        <Row label="Last updated">
          {account.sentiment_updated_at ? (
            <span className="text-sm text-muted-foreground">
              {new Date(account.sentiment_updated_at).toLocaleDateString()}
              {account.sentiment_updated_by_username && (
                <> by <span className="text-foreground">{account.sentiment_updated_by_username}</span></>
              )}
            </span>
          ) : (
            <Dash />
          )}
        </Row>
        <Row label="Do Not Contact">
          <div className="flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={account.do_not_contact}
              onClick={() => onSave({ do_not_contact: !account.do_not_contact })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                account.do_not_contact ? 'bg-red-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  account.do_not_contact ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-xs text-muted-foreground">
              {account.do_not_contact
                ? 'No automated outreach should be sent.'
                : 'Outreach allowed.'}
            </span>
          </div>
        </Row>
        {account.do_not_contact && (
          <Row label="DNC reason">
            <InlineText
              value={account.do_not_contact_reason}
              multiline
              onSave={(v) => onSave({ do_not_contact_reason: v })}
              placeholder="Why this account is on Do Not Contact (GDPR audit)"
            />
          </Row>
        )}
      </Section>

      <Section title="Contact">
        <Row label="Email">
          <InlineText value={account.email} onSave={(v) => onSave({ email: v })} placeholder="contact@company.com" />
        </Row>
        <Row label="Phone">
          <InlineText value={account.phone} onSave={(v) => onSave({ phone: v })} />
        </Row>
      </Section>

      <Section title="Address">
        <Row label="Street">
          <InlineText value={account.address_line1} onSave={(v) => onSave({ address_line1: v })} />
        </Row>
        <Row label="City">
          <InlineText value={account.city} onSave={(v) => onSave({ city: v })} />
        </Row>
        <Row label="Postal code">
          <InlineText value={account.postal_code} onSave={(v) => onSave({ postal_code: v })} />
        </Row>
        <Row label="Country (ISO 2)">
          <InlineText
            value={account.country_code}
            onSave={(v) => onSave({ country_code: v.toUpperCase().slice(0, 2) })}
            className="font-mono"
          />
        </Row>
      </Section>

      <Section title="Notes">
        <Row label="Notes">
          <InlineText
            value={account.notes}
            multiline
            onSave={(v) => onSave({ notes: v })}
            placeholder="Internal notes about this account"
          />
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Contacts tab ───────────────────
type ContactDraft = {
  first_name: string
  last_name: string
  email: string
  phone: string
  mobile: string
  job_title: string
  is_primary: boolean
  is_active: boolean
  notes: string
}

function emptyContactDraft(): ContactDraft {
  return {
    first_name: '', last_name: '', email: '', phone: '', mobile: '',
    job_title: '', is_primary: false, is_active: true, notes: '',
  }
}

function ContactsTab({ accountId }: { accountId: number }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const contactsQ = useQuery({
    queryKey: ['crm', 'contacts', { account: accountId }],
    queryFn: async () => (await api.get<Contact[]>(`/crm/contacts/?account=${accountId}`)).data,
  })

  const [editing, setEditing] = useState<Contact | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<ContactDraft>(emptyContactDraft())

  function openCreate() {
    setEditing(null)
    setDraft(emptyContactDraft())
    setCreating(true)
  }
  function openEdit(c: Contact) {
    setCreating(false); setEditing(c)
    setDraft({
      first_name: c.first_name, last_name: c.last_name,
      email: c.email, phone: c.phone, mobile: c.mobile,
      job_title: c.job_title, is_primary: c.is_primary,
      is_active: c.is_active, notes: c.notes,
    })
  }
  function closeDrawer() { setCreating(false); setEditing(null) }

  const createMut = useMutation({
    mutationFn: async () => (await api.post('/crm/contacts/', {
      ...draft, account: accountId, owner: null,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'contacts'] })
      qc.invalidateQueries({ queryKey: ['crm', 'account', accountId] })
      toast.success('Contact added'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const editMut = useMutation({
    mutationFn: async () => (await api.patch(`/crm/contacts/${editing!.id}/`, {
      ...draft, account: accountId,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'contacts'] })
      toast.success('Contact saved'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const inlineMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<Contact> }) =>
      (await api.patch(`/crm/contacts/${id}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'contacts'] }),
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/crm/contacts/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'contacts'] })
      qc.invalidateQueries({ queryKey: ['crm', 'account', accountId] })
      toast.success('Contact deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const saveField = async (id: number, payload: Partial<Contact>) => {
    await inlineMut.mutateAsync({ id, payload })
  }

  const contacts = contactsQ.data || []

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          People at this account. Mark one as primary — that person is the default for new activities and quotes.
        </p>
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add contact</Button>
      </div>

      {contactsQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : contacts.length === 0 ? (
        <p className="text-muted-foreground text-sm">No contacts at this account yet.</p>
      ) : (
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40">
            <tr>
              <Th>Name</Th><Th>Job title</Th><Th>Email</Th>
              <Th>Phone</Th><Th>Mobile</Th><Th className="text-center">Primary</Th><Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {contacts.map((c) => (
              <tr key={c.id} className={c.is_active ? '' : 'opacity-60'}>
                <Td className="font-medium">
                  <div className="text-foreground">{c.full_name}</div>
                  {!c.is_active && <div className="text-xs text-muted-foreground">inactive</div>}
                </Td>
                <Td className="text-xs">
                  <InlineText value={c.job_title} onSave={(v) => saveField(c.id, { job_title: v })} />
                </Td>
                <Td className="text-xs">
                  <InlineText value={c.email} onSave={(v) => saveField(c.id, { email: v })} />
                </Td>
                <Td className="text-xs">
                  <InlineText value={c.phone} onSave={(v) => saveField(c.id, { phone: v })} />
                </Td>
                <Td className="text-xs">
                  <InlineText value={c.mobile} onSave={(v) => saveField(c.id, { mobile: v })} />
                </Td>
                <Td className="text-center">
                  <button
                    type="button"
                    onClick={() => saveField(c.id, { is_primary: !c.is_primary })}
                    title={c.is_primary ? 'Primary' : 'Mark as primary'}
                    className="p-1 rounded hover:bg-muted"
                  >
                    <Star className={`h-4 w-4 ${c.is_primary ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground'}`} />
                  </button>
                </Td>
                <Td className="text-right">
                  <RowActions
                    onEdit={() => openEdit(c)}
                    onDelete={() => confirm(
                      `contact ${c.full_name}`,
                      () => deleteMut.mutate(c.id),
                    )}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <RecordDrawer
        open={creating || editing !== null}
        onOpenChange={(v) => { if (!v) closeDrawer() }}
        title={editing ? 'Edit contact' : 'New contact'}
        description={editing ? 'Update this contact.' : 'Add a contact at this account.'}
        onSubmit={() => editing ? editMut.mutate() : createMut.mutate()}
        isPending={createMut.isPending || editMut.isPending}
        submitLabel={editing ? 'Save' : 'Add contact'}
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="ct-first">First name *</FieldLabel>
              <Input id="ct-first" autoFocus required value={draft.first_name}
                onChange={(e) => setDraft({ ...draft, first_name: e.target.value })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="ct-last">Last name *</FieldLabel>
              <Input id="ct-last" required value={draft.last_name}
                onChange={(e) => setDraft({ ...draft, last_name: e.target.value })} />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="ct-job">Job title</FieldLabel>
            <Input id="ct-job" value={draft.job_title}
              onChange={(e) => setDraft({ ...draft, job_title: e.target.value })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="ct-email">Email</FieldLabel>
            <Input id="ct-email" type="email" value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="ct-phone">Phone</FieldLabel>
              <Input id="ct-phone" type="tel" value={draft.phone}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="ct-mobile">Mobile</FieldLabel>
              <Input id="ct-mobile" type="tel" value={draft.mobile}
                onChange={(e) => setDraft({ ...draft, mobile: e.target.value })} />
            </Field>
          </div>
          <Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.is_primary}
                onChange={(e) => setDraft({ ...draft, is_primary: e.target.checked })} />
              Primary contact
            </label>
            <FieldDescription>Default person for new activities and quotes.</FieldDescription>
          </Field>
          <Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.is_active}
                onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
              Active
            </label>
          </Field>
          <Field>
            <FieldLabel htmlFor="ct-notes">Notes</FieldLabel>
            <Textarea id="ct-notes" rows={3} value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// ─────────────────── Opportunities tab ───────────────────
// Read-only here. Opportunities have their own Record Page (Phase 3b).

function OpportunitiesTab({ opportunities, loading }:
  { opportunities: Opportunity[]; loading: boolean }) {
  const navigate = useNavigate()

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground max-w-2xl">
        Sales opportunities for this account. Click a row to open it. Create new opportunities from the Opportunities page.
      </p>

      {opportunities.length === 0 ? (
        <p className="text-muted-foreground text-sm">No opportunities yet.</p>
      ) : (
        <div className="bg-card rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>Name</Th>
                <Th>Stage</Th>
                <Th className="text-right">Amount</Th>
                <Th className="text-right">Weighted</Th>
                <Th>Expected close</Th>
                <Th>Owner</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {opportunities.map((o) => (
                <tr
                  key={o.id}
                  className="border-t border-border hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/crm/opportunities/${o.id}`)}
                  title="Open opportunity"
                >
                  <Td className="font-medium">{o.name}</Td>
                  <Td className="text-xs">
                    <span className="px-2 py-0.5 rounded font-semibold text-xs"
                      style={{ backgroundColor: o.stage_color, color: 'white' }}>
                      {o.stage_name}
                    </span>
                  </Td>
                  <Td className="text-right font-mono text-xs">
                    {Number(o.amount).toFixed(2)} {o.currency_code}
                  </Td>
                  <Td className="text-right font-mono text-xs text-muted-foreground">
                    {Number(o.weighted_amount).toFixed(2)}
                  </Td>
                  <Td className="text-xs">{o.expected_close_date || <Dash />}</Td>
                  <Td className="text-xs">{o.owner_username || <Dash />}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${OPP_STATUS_COLOR[o.status]}`}>
                      {o.status}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
